import { useSurveyAnswers } from "src/hooks/survey/answers";
import i18n from "src/locales";
import { getLocale } from "src/utils/meristemContext";
import { genderConstants, getPluralGenderedNoun } from "src/utils/sexAndGender";
import { usesCapitalizedNouns } from "src/utils/userSegment";

const PEOPLE_HELPED_MAPPING = {
  male_20s: 46425,
  male_30s: 143766,
  male_40s: 166325,
  male_50s: 140820,
  male_60s: 77645,
  "male_70+": 25703,
  male_70s: 25703,
  "male_80s+": 25703,
  female_20s: 284774,
  female_30s: 744788,
  female_40s: 801508,
  female_50s: 679366,
  female_60s: 382049,
  "female_70+": 111131,
  female_70s: 111131,
  "female_80s+": 111131,
  "20s": 331545,
  "30s": 888958,
  "40s": 968553,
  "50s": 820399,
  "60s": 459883,
  "70+": 136886,
  "70s": 136886,
  "80s+": 136886,
};

export function transformAgeYearsToAgeRange(ageYears?: number): string | null {
  if (ageYears == null) return null;
  if (ageYears < 30) return "20s";
  if (ageYears >= 80) return "80s+";

  const decade = Math.floor(ageYears / 10) * 10;
  return `${decade}s`;
}

function getPeopleHelped(ageGroup?: string, gender?: string): string {
  if (ageGroup) {
    const peopleHelpedAccessor = [
      genderConstants.male,
      genderConstants.female,
    ].includes(genderConstants[gender])
      ? `${gender}_${ageGroup}`
      : ageGroup;
    const number = PEOPLE_HELPED_MAPPING[peopleHelpedAccessor];
    if (number) {
      return new Intl.NumberFormat(getLocale()).format(number);
    }
  }
  return "";
}

function getAgeText(ageGroup?: string): string {
  if (ageGroup) {
    if (ageGroup.includes("70+") || ageGroup.includes("80s+")) {
      return i18n.t(`survey:questions:loading:agePhrase:${ageGroup}`);
    }
    return i18n.t("survey:questions:loading:agePhrase:default", {
      ageGroup,
    });
  }
  return "";
}

function usePeopleHelpedValues() {
  const { ageRange, ageYears, gender } = useSurveyAnswers();

  const ageParsed = ageRange?.[0] ?? transformAgeYearsToAgeRange(ageYears);

  const ageGroup = ageParsed
    ? i18n.t(`survey:pages:ageRange:options:${ageParsed}`)
    : null;
  const age = getAgeText(ageGroup);

  const genderParsed = gender?.[0];
  // Lowercase copy except for German language, which title-cases its gendered copy
  const genderedNoun = getPluralGenderedNoun(
    genderParsed,
    !usesCapitalizedNouns()
  );

  const peopleHelped = getPeopleHelped(ageParsed, genderParsed);

  return {
    peopleHelped,
    genderedNoun,
    age,
  };
}

export default usePeopleHelpedValues;
